import { useCallback, useEffect, useState } from 'react';
import { SlideModal } from '@podus/ui';
import { LuInfo, LuUser, LuUsers, LuCircleAlert, LuPaperclip, LuWallet } from 'react-icons/lu';
import { api, getBaseUrl } from '@/lib/api';
import StatusBadge from '@/components/StatusBadge';
import type { Case, CaseSchedule, CaseTimelineEntry, Transaction } from '@/types';

export type CaseStatus = Case['status'];

export interface UserCaseRow {
    id: number;
    caseNumber?: string;
    title: string;
    date: string;
    disputeCategory: string;
    amount: string;
    status: Case['status'];
}

export interface CaseViewModalProps {
    open: boolean;
    onClose: () => void;
    caseData: UserCaseRow | null;
}

type TabId = 'details' | 'schedule' | 'timeline' | 'financials';

const MODAL_TABS: { id: TabId; label: string }[] = [
    { id: 'details', label: 'Case Details' },
    { id: 'schedule', label: 'Schedule' },
    { id: 'timeline', label: 'Case Timeline' },
    { id: 'financials', label: 'Financials' },
];

const SCHEDULE_COLOR_MAP: Record<string, string> = {
    blue: 'bg-blue-600',
    orange: 'bg-primary',
    green: 'bg-green-600',
    red: 'bg-red-600',
    purple: 'bg-purple-600',
};

function formatDate(iso: string): string {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatTime(time: string): string {
    const [h, m] = time.split(':');
    const hour = parseInt(h, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const h12 = hour % 12 || 12;
    return `${h12}:${m} ${ampm}`;
}

function SectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
    return (
        <div className="rounded-xl border border-[#eee] p-5">
            <div className="mb-4 flex items-center gap-2 border-b border-[#eee] pb-3">
                {icon}
                <h3 className="text-[14px] font-bold text-secondary">{title}</h3>
            </div>
            {children}
        </div>
    );
}

function Field({ label, value }: { label: string; value: string }) {
    return (
        <div>
            <p className="text-[12px] font-semibold text-secondary">{label}</p>
            <p className="mt-1 text-[13px] text-[#555]">{value || '—'}</p>
        </div>
    );
}

/* ─── Tab Content ─── */

function CaseDetailsTab({ caseRow, fullCase }: { caseRow: UserCaseRow; fullCase: Case | null }) {
    const applicant = fullCase?.parties?.find((p) => p.role === 'Applicant' || p.role === 'Claimant') ?? null;
    const respondent = fullCase?.parties?.find((p) => p.role === 'Respondent' || p.role === 'Defendant') ?? null;

    return (
        <div className="space-y-5">
            <SectionCard icon={<LuInfo size={18} className="text-secondary" />} title="General">
                <div className="grid grid-cols-2 gap-y-4">
                    <Field label="Date" value={caseRow.date} />
                    <Field label="Amount Paid" value={caseRow.amount} />
                    {fullCase?.caseNumber && <Field label="Case Number" value={fullCase.caseNumber} />}
                </div>
                {fullCase?.description && (
                    <div className="mt-4">
                        <p className="text-[12px] font-semibold text-secondary">Description</p>
                        <p className="mt-1 text-[13px] leading-relaxed text-[#555]">{fullCase.description}</p>
                    </div>
                )}
            </SectionCard>

            {applicant && (
                <SectionCard icon={<LuUser size={18} className="text-secondary" />} title="Applicant">
                    <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-3">
                        <Field label="Name" value={applicant.name} />
                        <Field label="Party Type" value={applicant.partyType} />
                        <Field label="Email" value={applicant.email} />
                        <Field label="Phone Number" value={applicant.phone} />
                        <Field label="Address" value={applicant.address} />
                    </div>
                </SectionCard>
            )}

            {respondent && (
                <SectionCard icon={<LuUsers size={18} className="text-secondary" />} title="Respondent">
                    <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-3">
                        <Field label="Name" value={respondent.name} />
                        <Field label="Party Type" value={respondent.partyType} />
                        <Field label="Email" value={respondent.email} />
                        <Field label="Phone Number" value={respondent.phone} />
                        <Field label="Address" value={respondent.address} />
                    </div>
                </SectionCard>
            )}

            <SectionCard icon={<LuCircleAlert size={18} className="text-secondary" />} title="Dispute">
                <div className="grid grid-cols-2 gap-y-4">
                    <Field label="Dispute Category" value={caseRow.disputeCategory} />
                </div>
            </SectionCard>

            {fullCase?.evidence && fullCase.evidence.length > 0 && (
                <SectionCard icon={<LuPaperclip size={18} className="text-secondary" />} title="Evidence">
                    {(() => {
                        const mandatory = fullCase.evidence.filter((ev) => ev.mandatoryDocType);
                        const others = fullCase.evidence.filter((ev) => !ev.mandatoryDocType);
                        const fileLink = (ev: (typeof fullCase.evidence)[number]) => {
                            const href = ev.fileUrl ? `${getBaseUrl()}${ev.fileUrl}` : undefined;
                            return href ? (
                                <a
                                    href={href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-secondary underline underline-offset-2 break-all"
                                >
                                    {ev.fileName}
                                </a>
                            ) : (
                                <span className="text-[#555] break-all">{ev.fileName}</span>
                            );
                        };
                        return (
                            <div className="space-y-3">
                                {mandatory.map((ev) => (
                                    <div key={ev.id} className="flex items-baseline gap-2 text-[13px]">
                                        <span className="font-semibold text-secondary">{ev.mandatoryDocType}:</span>
                                        {fileLink(ev)}
                                    </div>
                                ))}
                                {others.length > 0 && (
                                    <div className={mandatory.length > 0 ? 'mt-2' : ''}>
                                        {mandatory.length > 0 && (
                                            <p className="mb-2 text-[12px] font-semibold text-secondary">
                                                Additional files
                                            </p>
                                        )}
                                        <div className="flex flex-wrap gap-2">
                                            {others.map((ev) => {
                                                const href = ev.fileUrl ? `${getBaseUrl()}${ev.fileUrl}` : undefined;
                                                return href ? (
                                                    <a
                                                        key={ev.id}
                                                        href={href}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="rounded-md bg-[#f5f5f5] px-3 py-1.5 text-[12px] text-secondary underline underline-offset-2 hover:bg-[#eee]"
                                                    >
                                                        {ev.fileName}
                                                    </a>
                                                ) : (
                                                    <span
                                                        key={ev.id}
                                                        className="rounded-md bg-[#f5f5f5] px-3 py-1.5 text-[12px] text-[#555]"
                                                    >
                                                        {ev.fileName}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })()}
                </SectionCard>
            )}
        </div>
    );
}

function ScheduleTab({ schedules }: { schedules: CaseSchedule[] }) {
    if (schedules.length === 0) {
        return <p className="py-8 text-center text-[13px] text-[#999]">No schedules yet.</p>;
    }

    const grouped = schedules.reduce<Record<string, CaseSchedule[]>>((acc, s) => {
        const key = formatDate(s.scheduledDate);
        (acc[key] ??= []).push(s);
        return acc;
    }, {});

    return (
        <div className="space-y-5">
            {Object.entries(grouped).map(([date, events]) => (
                <div key={date} className="rounded-xl border border-[#eee] p-5">
                    <h3 className="mb-4 text-[14px] font-bold text-secondary">{date}</h3>
                    <div className="divide-y divide-background">
                        {events.map((event) => (
                            <div key={event.id} className="flex items-center gap-4 py-3">
                                <span className="text-[13px] text-[#888]">{formatTime(event.scheduledTime)}</span>
                                <div
                                    className={`h-8 w-1 rounded-full ${
                                        SCHEDULE_COLOR_MAP[event.color ?? ''] ?? 'bg-blue-600'
                                    }`}
                                />
                                <div>
                                    <p className="text-[14px] font-semibold text-secondary">{event.title}</p>
                                    {event.note && <p className="text-[11px] text-primary">{event.note}</p>}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ))}
        </div>
    );
}

function CaseTimelineTab({ timeline }: { timeline: CaseTimelineEntry[] }) {
    if (timeline.length === 0) {
        return <p className="py-8 text-center text-[13px] text-[#999]">No timeline entries yet.</p>;
    }
    return (
        <div className="rounded-xl border border-[#eee] p-5">
            <h3 className="mb-4 text-[14px] font-bold text-secondary">Case Timeline</h3>
            <ol className="relative ml-2 border-l border-[#eee] pl-5">
                {timeline.map((item) => (
                    <li key={item.id} className="mb-5 last:mb-0">
                        <span className="absolute -left-1.5 mt-1 h-3 w-3 rounded-full bg-primary" />
                        <p className="text-[12px] text-[#888]">{formatDate(item.createdAt)}</p>
                        <p className="mt-1 text-[14px] font-semibold text-secondary">{item.title}</p>
                        {item.detail && (
                            <p className="mt-2 text-[13px] leading-relaxed text-[#555]">{item.detail}</p>
                        )}
                    </li>
                ))}
            </ol>
        </div>
    );
}

function FinancialsTab({ transactions }: { transactions: Transaction[] }) {
    if (!transactions || transactions.length === 0) {
        return <p className="py-8 text-center text-[13px] text-[#999]">No transactions for this case.</p>;
    }

    const formatNaira = (n: number) => `₦${Number(n || 0).toLocaleString()}`;
    const formatDateTime = (iso: string) =>
        new Date(iso).toLocaleString('en-GB', {
            hour: '2-digit',
            minute: '2-digit',
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });

    return (
        <div className="space-y-5">
            {transactions.map((tx) => (
                <SectionCard key={tx.id} icon={<LuWallet size={18} className="text-secondary" />} title="Transaction">
                    <div className="grid grid-cols-2 gap-y-4 sm:grid-cols-3">
                        <Field label="Transaction ID" value={tx.transactionId} />
                        <Field label="Date & Time" value={formatDateTime(tx.createdAt)} />
                        <Field label="Amount Paid" value={formatNaira(tx.amountPaid)} />
                        <Field label="Payment Method" value={tx.paymentMethod} />
                    </div>
                </SectionCard>
            ))}
        </div>
    );
}

/* ─── Main Modal ─── */

export default function CaseViewModal({ open, onClose, caseData }: CaseViewModalProps) {
    const [activeTab, setActiveTab] = useState<TabId>('details');
    const [fullCase, setFullCase] = useState<Case | null>(null);

    const fetchFullCase = useCallback(async () => {
        if (!caseData?.id) return;
        try {
            const res = await api.get<Case>(`/api/cases/${caseData.id}`);
            setFullCase(res.response.data ?? null);
        } catch {
            setFullCase(null);
        }
    }, [caseData?.id]);

    useEffect(() => {
        if (!open || !caseData?.id) return;
        setFullCase(null);
        void fetchFullCase();
    }, [open, caseData?.id, fetchFullCase]);

    if (!caseData) return null;

    const statusBadge = <StatusBadge status={caseData.status} />;

    return (
        <SlideModal
            open={open}
            onClose={() => {
                setActiveTab('details');
                setFullCase(null);
                onClose();
            }}
            title={<h2 className="text-lg font-bold text-secondary">{caseData.title}</h2>}
            headerRight={statusBadge}
            maxWidth="max-w-3xl"
        >
            <div className="flex flex-1 flex-col overflow-hidden lg:flex-row">
                <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-[#eee] px-4 pt-2 lg:flex-col lg:gap-0 lg:overflow-x-visible lg:border-b-0 lg:border-r lg:pt-6">
                    {MODAL_TABS.map((tab) => (
                        <button
                            key={tab.id}
                            type="button"
                            onClick={() => setActiveTab(tab.id)}
                            className={`whitespace-nowrap px-3 py-2 text-[13px] font-medium transition-colors lg:w-full lg:text-left ${
                                activeTab === tab.id ? 'text-primary' : 'text-[#555] hover:text-secondary'
                            }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </nav>

                <div className="flex-1 overflow-y-auto p-4 lg:p-6">
                    {activeTab === 'details' && <CaseDetailsTab caseRow={caseData} fullCase={fullCase} />}
                    {activeTab === 'schedule' && <ScheduleTab schedules={fullCase?.schedules ?? []} />}
                    {activeTab === 'timeline' && <CaseTimelineTab timeline={fullCase?.timeline ?? []} />}
                    {activeTab === 'financials' && <FinancialsTab transactions={fullCase?.transactions ?? []} />}
                </div>
            </div>
        </SlideModal>
    );
}
